/* eslint-disable max-len */
import { useAgentSendMessage } from '@inwink/aiinputs/inwinkagent';
import type { IAgentTool, IToolResultRendererProps } from '@inwink/aiinputs/inwinkagent/inwinkagent.types';
import { AppTextLabel } from '@@services/i18nservice';
import * as React from 'react';

import "./tools.suggestions.less";

interface IQuickReplyOption {
    label: string;
    value: string;
}

interface IQuickReplyArgs {
    prompt?: string;
    options: IQuickReplyOption[];
    mode?: 'single' | 'multiple';
}

export const QuickRepliesResultRenderer = (props: IToolResultRendererProps) => {
    const sendMessage = useAgentSendMessage();
    const [answered, setAnswered] = React.useState(false);
    const [selected, setSelected] = React.useState<string[]>([]);

    const args: IQuickReplyArgs = React.useMemo(() => {
        try {
            return JSON.parse(props.result || props.args as any || '{}');
        } catch {
            return props.args as IQuickReplyArgs;
        }
    }, [props.result, props.args]);

    const options = args.options;
    const mode = args.mode || 'single';

    const handleSelect = React.useCallback((value: string) => {
        if (answered || props.runStatus === 'running') return;

        if (mode === 'single') {
            const option = options?.find((o) => o.value === value);
            setSelected([value]);
            setAnswered(true);
            sendMessage({ content: option?.label ?? value });
        } else {
            setSelected((prev) =>
                prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
            );
        }
    }, [answered, mode, options, props.runStatus, sendMessage]);

    const handleValidate = React.useCallback(() => {
        if (answered || selected.length === 0) return;
        const labels = selected.map((v) => options?.find((o) => o.value === v)?.label ?? v);
        setAnswered(true);
        sendMessage({ content: labels.join(', ') });
    }, [answered, selected, options, sendMessage]);

    if (!options?.length || props.runStatus === 'running' || answered || !props.isLastMessage) {
        return null;
    }

    return (
        <div className="card inwinkagent-chat-toolcall-card">
            {args.prompt && <div className="inwinkagent-chat-toolcall-card-remark">{args.prompt}</div>}
            {!answered && <div className="inwinkagent-chat-toolcall-card-chips">
                {options.map((option) => {
                    const isSelected = selected.includes(option.value);
                    return (
                        <button
                            key={option.value}
                            className={`inwinkagent-chat-toolcall-card-chip${isSelected ? ' selected' : ''}`}
                            disabled={answered || props.runStatus === 'running'}
                            onClick={() => handleSelect(option.value)}
                        >
                            {option.label}
                        </button>
                    );
                })}
            </div>}
            {mode === 'multiple' && !answered && (
                <button
                    className="inwinkagent-chat-toolcall-card-mainaction"
                    disabled={selected.length === 0}
                    onClick={handleValidate}
                >
                    <AppTextLabel i18n="actions.validate" />
                </button>
            )}
        </div>
    );
};

const optionsSchema = {
    type: 'object',
    additionalProperties: false,
    properties: {
        options: {
            type: 'array',
            minItems: 1,
            items: {
                type: 'object',
                additionalProperties: false,
                properties: {
                    label: { type: 'string', minLength: 1 },
                    value: { type: 'string', minLength: 1 }
                },
                required: ['label', 'value']
            }
        },
        mode: {
            type: 'string',
            enum: ['single', 'multiple'],
            default: "multiple",
            description: "\"multiple\" (DEFAULT, PREFERRED): user selects one or more options, then validates. " 
                + "Use for interests, goals, topics, preferences — any question where several answers make sense. " 
                + "\"single\": user clicks one option and it submits immediately. " 
                + "Use ONLY for binary yes/no, mutually exclusive picks (e.g., choose one biography style), or navigation shortcuts."
        },
        prompt: {
            type: 'string',
            description: 'Optional question displayed before options. Be mindful that this prompt and options will be displayed AFTER your reply'
        }
    },
    required: ['options', 'mode']
};

export const quickRepliesTool: IAgentTool = {
    name: 'suggestRepliesOptions',
    description:
        "Renders clickable suggestion chips after your reply. The user picks from the options instead of typing. " 
        + "ALWAYS set mode: prefer \"multiple\" when the user can reasonably select several answers (interests, goals, topics, preferences). " 
        + "Use \"single\" only for mutually exclusive choices (yes/no, pick-one).",
    parameters: optionsSchema,
    toolResultRenderer: QuickRepliesResultRenderer,
};
